// 05 — Chore sheet. A bottom sheet over the dimmed coop: the chore, the meter
// that says why it is due, one tap to do it, the optional minigame, and the
// fact card behind "Why?".

import type { ChoreId } from '../../core/types';
import { t } from '../../i18n';
import { completeChore } from '../actions';
import { playChoreDone } from '../sound';
import { bar } from '../components/bar';
import { factCard } from '../components/factCard';
import { openSheet } from '../components/sheet';
import { button, div, h, icon } from '../dom';
import { openMinigameSheet } from './minigameSheet';
import { selectChoreSheet } from '../selectors';
import type { ScreenContext } from '../router';

const CHECK_ICON = '<path d="M5 12l4 4 10-10"></path>';
const PLAY_ICON = '<path d="M8 5v14l11-7z"></path>';

/**
 * Opens the sheet for one chore. Not a route: the coop screen stays mounted
 * behind the backdrop, with the world still running underneath (PLAN-03 §4).
 */
export function openChoreSheet(ctx: ScreenContext<HTMLElement>, chore: ChoreId): void {
  const { store, shell } = ctx.services;
  const view = selectChoreSheet(store.getState(), chore);
  const sheet = openSheet(shell, {
    title: t(view.titleKey),
    onClose: () => {
      // Nothing to undo: the sheet only ever completed the chore it was opened
      // for, and it said so before the tap.
    },
  });

  if (view.meter) {
    sheet.body.appendChild(
      div(
        { class: 'cf-card cf-stack cf-stack--tight' },
        bar({
          label: view.meter.label,
          value: view.meter.value,
          valueLabel: t(view.meter.valueKey, { value: view.meter.value }),
          tone: view.meter.tone,
        }),
        h('p', { class: 'cf-small', text: t(view.reasonKey, view.reasonParams) }),
      ),
    );
  }

  const quick = button(
    {
      class: 'cf-actionrow cf-actionrow--primary',
      on: { click: () => quickDone() },
    },
    icon({ shapes: CHECK_ICON, size: 28, tone: 'white', strokeWidth: 2.4 }),
    div(
      { class: 'cf-stack cf-stack--tight cf-grow' },
      h('span', { class: 'cf-actionrow__title', text: t(view.quickKey) }),
      h('span', { class: 'cf-actionrow__sub', text: t('choreSheet.quickSub') }),
    ),
  );
  sheet.body.appendChild(quick);

  // The Play button only exists where a minigame does. A chore without one is
  // a one-tap chore, and the sheet does not offer a greyed-out button for it.
  if (view.minigame && view.minigameKey && view.minigameHintKey) {
    const minigame = view.minigame;
    sheet.body.appendChild(
      button(
        {
          class: 'cf-actionrow cf-actionrow--good',
          on: {
            click: () => {
              sheet.close();
              openMinigameSheet(ctx, minigame, chore);
            },
          },
        },
        icon({ shapes: PLAY_ICON, size: 28, tone: 'sage', strokeWidth: 2.2 }),
        div(
          { class: 'cf-stack cf-stack--tight cf-grow' },
          h('span', { class: 'cf-actionrow__title', text: t(view.minigameKey) }),
          h('span', { class: 'cf-actionrow__sub', text: t(view.minigameHintKey) }),
        ),
      ),
    );
  }

  if (view.fact) {
    sheet.body.appendChild(
      button({
        class: 'cf-btn cf-btn--quiet cf-sheet__why',
        text: t('choreSheet.why'),
        on: {
          click: () => {
            sheet.close();
            const card = openSheet(shell, { title: t(view.fact ? view.fact.id : ''), onClose: () => undefined });
            card.body.replaceChildren(factCard(view.fact!, store, card.close));
          },
        },
      }),
    );
  }

  function quickDone(): void {
    const result = completeChore(store, chore);
    if (result.performed) {
      playChoreDone(store);
      sheet.close();
      shell.toast(t('chore.doneToast', { chore: t(`chore.${chore}`) }));
    } else if (result.messageKey) {
      // The sheet stays open: the player still has not done the chore, and the
      // message says what is missing.
      shell.toast(t(result.messageKey, result.messageParams));
    }
  }
}
